import { clsx } from 'clsx';
import { type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes, useId } from 'react';

/**
 * Labelled input with hint and error. 48 px tall, 16 px text (no iOS zoom), ink outline. The
 * error sits in a peach box with a "!" so it reads without color, and is wired with
 * aria-invalid and aria-describedby.
 *
 *   <TextField label="Nickname" hint="What the crew yells" value={v} onChange={(e) => setV(e.target.value)} />
 *   <TextArea label="Rules" rows={4} error="Too long, coach." />
 */
interface FieldBits {
  label: string;
  hint?: string;
  error?: string | null;
  /** Hide the label visually (it stays for screen readers). */
  hideLabel?: boolean;
  className?: string;
}

function useFieldIds(hint?: string, error?: string | null) {
  const id = useId();
  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-err` : null].filter(Boolean).join(' ') || undefined;
  return { id, describedBy };
}

function FieldFrame({
  id,
  label,
  hint,
  error,
  hideLabel,
  className,
  children,
}: FieldBits & { id: string; children: ReactNode }) {
  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className={hideLabel ? 'sr-only' : 'text-sm font-bold uppercase tracking-wide'}>
        {label}
      </label>
      {children}
      {hint && !error && (
        <p id={`${id}-hint`} className="text-sm text-ink/75">
          {hint}
        </p>
      )}
      {error && (
        <p
          id={`${id}-err`}
          className="flex items-start gap-2 rounded-chip border-2 border-ink bg-peach px-2 py-1 text-sm font-semibold"
        >
          <span aria-hidden="true" className="font-display font-black">
            !
          </span>
          {error}
        </p>
      )}
    </div>
  );
}

export type TextFieldProps = FieldBits & Omit<InputHTMLAttributes<HTMLInputElement>, 'className'>;

export function TextField({ label, hint, error, hideLabel, className, ...input }: TextFieldProps) {
  const { id, describedBy } = useFieldIds(hint, error);
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} hideLabel={hideLabel} className={className}>
      <input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        {...input}
        className={clsx('tts-input text-base', error && 'border-(length:--tts-border-heavy)')}
      />
    </FieldFrame>
  );
}

export type TextAreaProps = FieldBits & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'>;

export function TextArea({ label, hint, error, hideLabel, className, rows = 4, ...input }: TextAreaProps) {
  const { id, describedBy } = useFieldIds(hint, error);
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} hideLabel={hideLabel} className={className}>
      <textarea
        id={id}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        {...input}
        className="tts-input py-2.5 text-base leading-snug"
      />
    </FieldFrame>
  );
}
